import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'components/forms/NumberSelector';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { GenericLoading } from 'components/ui/Loader';
import { useSubscriptionProcess } from '../contexts/SubscriptionProcessContext';
import {
  useIsContractSigned,
  useIsQuoteCancelled,
  useIsQuoteRefused,
  useCanAbandonQuote,
  useQuoteContractualizationData
} from './store';
import { AbandonQuoteButton } from './components/AbandonQuoteButton';
import { CancelledQuoteBanner } from './components/CancelledQuoteBanner';
import { RefusedQuoteBanner } from './components/RefusedQuoteBanner';
import { QuoteDetails } from './components/QuoteDetails';
import {
  ContractualizationLayout,
  ContractualizationHeader,
  FileGroupSelector,
  FileGroupTitle
} from './components/layout';
import {
  ContractInformation,
  ContractSignature,
  ContractSimulations,
  ValidationSection
} from './components/sections';
import { ContractualizationResponseDto } from 'api/quote';
import { useFileGroup } from './hooks/useFileGroup';
import { isFileGroupSwitch } from '../utils/fileGroupSwitch';

/**
 * Main contract management component that orchestrates the entire contract flow
 *
 * Data flow:
 * - Quote data comes from contractualizationStore (single source of truth)
 * - Store is populated during fetchSubscriptionProcess
 * - Documents and UI state also live in contractualizationStore
 */
export const ContractualizationStepContent = () => {
  const { riskId, quoteId } = useParams<{ riskId: string; quoteId?: string }>();
  const location = useLocation();
  const { openPanel } = usePanel();
  const { t } = useDynamicTranslation([NsKey.Contractualization]);

  const { state: subscriptionState } = useSubscriptionProcess();

  const contractualizationData = subscriptionState.contractualizationData as ContractualizationResponseDto;

  const isContractSigned = useIsContractSigned();
  const isCancelled = useIsQuoteCancelled();
  const isRefused = useIsQuoteRefused();
  const canAbandon = useCanAbandonQuote();

  // The store copy of the quote data is replaced on every reload of the page data (quote status,
  // mandate, contract): the file statuses follow it.
  const quoteData = useQuoteContractualizationData();
  const fileGroup = useFileGroup(riskId, quoteData);

  const showQuoteDetails = useCallback(() => {
    if (contractualizationData)
      openPanel(
        {
          title: t('CONTRACTUALIZATION.TITLE.QUOTE_DETAILS'),
          size: Sizes['3xl']
        },
        <QuoteDetails quote={contractualizationData as ContractualizationResponseDto} />
      );
  }, [openPanel, contractualizationData, t]);

  const quoteInfo = useMemo(() => {
    if (contractualizationData) {
      // While another file of the group loads, the data on screen is still the previous quote's.
      const label = (
        contractualizationData.quoteTabs.find(tab => tab.id === quoteId)
        ?? contractualizationData.quoteTabs.find(tab => tab.id === contractualizationData.quoteId)
      )?.label;
      if (!label) return null;
      const splits = label.split('-');
      return {
        identifier: splits[3],
        fullLabel: label
      };
    }
    return null;
  }, [contractualizationData, quoteId]);

  // Show loading while subscription process is loading or data not available
  if (subscriptionState.isLoading || !contractualizationData) {
    return <GenericLoading loading isFullPage />;
  }

  // File tabs: the URL already names the other file while its data loads behind the page. Only a
  // navigation made by the file tabs counts, and only until the quote on screen is the one asked:
  // comparing risk ids alone dimmed the page for good on a plain arrival (seen on 21/09/2026).
  const isSwitchingFile =
    isFileGroupSwitch(location.state)
    && Boolean(quoteId)
    && contractualizationData.quoteId?.toLowerCase() !== quoteId?.toLowerCase();

  // Under the file tabs, the quotes are listed (as chips) when there are several of them: a single
  // quote is told by the file tab itself (product and price). Once the contract is signed, only its
  // quote remains. Without file tabs, the quote tabs keep their rule: several quotes, not signed.
  const hasFileTabs = fileGroup.length > 1;
  const quoteTabs = contractualizationData.quoteTabs || [];
  const shownQuoteTabs = hasFileTabs && isContractSigned
    ? quoteTabs.filter(tab => tab.id === contractualizationData.quoteId)
    : quoteTabs;
  const showQuoteTabs = hasFileTabs ? shownQuoteTabs.length > 1 : !isContractSigned && quoteTabs.length > 1;

  const tabsComponent = showQuoteTabs ? (
    <ContractSimulations
      tabs={shownQuoteTabs}
      activeTab={isSwitchingFile ? contractualizationData.quoteId : quoteId}
      asChips={hasFileTabs}
    />
  ) : undefined;

  const fileSelectorComponent =
    hasFileTabs ? <FileGroupSelector items={fileGroup} /> : undefined;

  const headerComponent =
    contractualizationData.quoteTabs?.length > 0 && quoteInfo ? (
      <ContractualizationHeader
        quoteData={contractualizationData}
        quoteIdentifier={quoteInfo.identifier}
        onShowDetails={showQuoteDetails}
        detailsButtonLabel={t('CONTRACTUALIZATION.LABELS.SEE_QUOTE_DETAILS')}
      />
    ) : undefined;

  return (
    <ContractualizationLayout
      fileSelectorTitle={hasFileTabs ? <FileGroupTitle items={fileGroup} /> : undefined}
      fileSelector={fileSelectorComponent}
      tabs={tabsComponent}
      header={headerComponent}
    >
      {isCancelled && <CancelledQuoteBanner />}
      {!isCancelled && isRefused && (
        <RefusedQuoteBanner validatorMessage={quoteData?.validation?.derniereDemande?.messageValideur} />
      )}
      {/*
        Devis abandonné ou refusé par le valideur : on fige les sections d'action
        (informations, validation, signature, documents). Le header et les onglets
        restent cliquables pour naviguer.
      */}
      <div
        aria-disabled={isCancelled || isRefused || isSwitchingFile || undefined}
        aria-busy={isSwitchingFile || undefined}
        className={clsx(
          // Same spacing as the layout column: the wrapper must not glue the sections together.
          'flex flex-col gap-4 transition-opacity duration-200',
          (isCancelled || isRefused || isSwitchingFile) && 'pointer-events-none select-none opacity-60'
        )}
      >
        <ContractInformation />
        <ValidationSection />
        <ContractSignature />
      </div>

      {/* Action discrète d'abandon du devis, en bas (CBNO / chômage / GLI). */}
      <AbandonQuoteButton
        devisId={contractualizationData.quoteId}
        canAbandon={canAbandon}
      />
    </ContractualizationLayout>
  );
};

export const ContractualizationStep = ContractualizationStepContent;
